<template>
  <Teleport to="body">
    <div class="toast-container" aria-live="assertive" aria-atomic="true">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toastStore.toasts"
          :key="toast.id"
          :class="['toast', `toast--${toast.type}`]"
          role="alert"
        >
          <span class="toast-icon"><AppIcon :name="iconFor(toast.type)" :size="16" /></span>
          <span class="toast-message">{{ toast.message }}</span>
          <button
            class="toast-close"
            @click="toastStore.remove(toast.id)"
            :aria-label="t('common.dismiss_notification')"
          >
            <X :size="14" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import type { ToastType } from '@/stores/toast'
import { useToastStore } from '@/stores/toast'
import AppIcon from '@/components/AppIcon.vue'
import { X } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const toastStore = useToastStore()

function iconFor(type: ToastType): string {
  switch (type) {
    case 'success':
      return 'toast-success'
    case 'error':
      return 'toast-error'
    case 'warning':
      return 'toast-warning'
    case 'info':
      return 'toast-info'
  }
}
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: calc(var(--header-height, 60px) + 12px);
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: var(--z-notification, 500);
  pointer-events: none;
  max-width: 380px;
  width: 100%;
}

.toast {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.5));
  color: var(--text-primary, #fff);
  font-size: var(--font-size-body, 0.875rem);
  font-family: var(--font-family-primary, 'Inter', sans-serif);
  pointer-events: auto;
}

.toast--success {
  border-left: 3px solid var(--color-success, #00e701);
}

.toast--error {
  border-left: 3px solid var(--color-danger, #ed4245);
}

.toast--warning {
  border-left: 3px solid var(--color-warning, #ffa726);
}

.toast--info {
  border-left: 3px solid var(--color-info, #1475e1);
}

.toast-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.toast--success .toast-icon {
  color: var(--color-success, #00e701);
}

.toast--error .toast-icon {
  color: var(--color-danger, #ed4245);
}

.toast--warning .toast-icon {
  color: var(--color-warning, #ffa726);
}

.toast--info .toast-icon {
  color: var(--color-info, #1475e1);
}

.toast-message {
  flex: 1;
  line-height: var(--line-height-body, 1.5);
}

.toast-close {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 12px;
  cursor: pointer;
  border-radius: var(--radius-sm, 4px);
  transition:
    color var(--transition-fast, 150ms ease),
    background var(--transition-fast, 150ms ease);
}

.toast-close:hover {
  color: var(--text-primary, #fff);
  background: rgba(255, 255, 255, 0.08);
}

/* TransitionGroup animations */
.toast-enter-active {
  animation: toastIn 250ms ease forwards;
}

.toast-leave-active {
  animation: toastOut 200ms ease forwards;
}

.toast-move {
  transition: transform 250ms ease;
}

@keyframes toastIn {
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes toastOut {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(40px);
  }
}

@media (max-width: 767px) {
  .toast-container {
    right: 8px;
    left: 8px;
    max-width: none;
  }
}
</style>
